import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ExecutionProviderBadge from '../components/ExecutionProviderBadge';
import OpenRecommendationsPanel from '../components/OpenRecommendationsPanel';
import ProjectRevisionsPanel from '../components/ProjectRevisionsPanel';
import bakingApi from '../services/bakingApi';
import {
  getProviderCapabilities,
  providerJobCreationEnabled,
  providerRuntimeOperationLabel,
} from '../baking/executionProviders';
import {
  isAiRiggingJobCreationEnabled,
  isAiRiggingUiEnabled,
  operationLabel,
  shouldShowProjectRiggingJobsNav,
  shouldShowProjectRiggingNav,
} from '../features/aiRigging';
import { useAuth } from '../context/AuthContext';
import {
  JOB_VIEW,
  PROJECT_RIG_MEMORY_VIEW,
  RIGGING_JOB_CREATE,
  RIGGING_JOB_VIEW,
  hasTenantPermission,
} from '../auth/permissions';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';
import RiggingJobStatusBadge from '../components/rigging/RiggingJobStatusBadge';
import { shortJobId } from '../features/riggingJobStatus';

function StatusPill({ status }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

function ProjectSettingsPage() {
  const { projectId } = useParams();
  const { activeTenant, hasTenantPermission: can, aiRiggingFlags } = useAuth();
  const [project, setProject] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [riggingJobsSummary, setRiggingJobsSummary] = useState(null);
  const [riggingJobsError, setRiggingJobsError] = useState(null);
  const [assetPath, setAssetPath] = useState('Assets/Scenes/Main.unity');
  const [displayName, setDisplayName] = useState('Main');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sceneError, setSceneError] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [compatibilityError, setCompatibilityError] = useState(null);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);

  const isMaya = project && project.execution_provider === 'maya';
  const providerCaps = project
    ? getProviderCapabilities(project.execution_provider)
    : null;
  const jobCreationEnabled = project
    ? providerJobCreationEnabled(project.execution_provider)
    : false;
  const runtimeLabel = project
    ? providerRuntimeOperationLabel(project.execution_provider)
    : 'Run job';
  const permissions = activeTenant?.permissions || [];
  const check = (perm) =>
    (typeof can === 'function' && can(perm)) || hasTenantPermission(permissions, perm);
  const canViewBakeJobs = check(JOB_VIEW);
  const canViewRiggingJobs = check(RIGGING_JOB_VIEW);
  const canCreateRiggingJobs =
    check(RIGGING_JOB_CREATE) && isAiRiggingJobCreationEnabled();
  const canViewRiggingConfig =
    check(PROJECT_RIG_MEMORY_VIEW) || check(RIGGING_JOB_VIEW);

  const aiEnabled =
    aiRiggingFlags != null
      ? Boolean(aiRiggingFlags.ai_rigging_enabled)
      : isAiRiggingUiEnabled();
  const memoryEnabled =
    aiRiggingFlags != null
      ? Boolean(aiRiggingFlags.project_rig_memory_enabled)
      : undefined;

  const showRiggingConfig =
    Boolean(project) &&
    canViewRiggingConfig &&
    shouldShowProjectRiggingNav({
      executionProvider: project.execution_provider,
      aiRiggingEnabled: aiEnabled,
      projectRigMemoryEnabled: memoryEnabled,
    });
  const showRiggingJobs =
    Boolean(project) &&
    canViewRiggingJobs &&
    shouldShowProjectRiggingJobsNav({
      executionProvider: project.execution_provider,
      aiRiggingEnabled: aiEnabled,
    });
  // Unity Bake Jobs card: Unity projects (or any provider with job creation).
  // Maya projects do not show Unity Bake Jobs unless job creation is enabled.
  const showBakeJobsCard = Boolean(project) && canViewBakeJobs && (!isMaya || jobCreationEnabled);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [projectData, revisionData, sceneData, jobData] = await Promise.all([
          bakingApi.getProject(projectId),
          bakingApi.listRevisions(projectId),
          bakingApi.listScenes(projectId),
          bakingApi.listJobs(),
        ]);
        if (cancelled) return;
        setProject(projectData);
        setRevisions(revisionData);
        setScenes(sceneData);
        setJobs(jobData.filter((job) => job.project === projectId));
        if (projectData.execution_provider === 'maya') {
          setAssetPath('scenes/main.ma');
          setDisplayName('main');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load project');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!showRiggingJobs) {
      setRiggingJobsSummary(null);
      setRiggingJobsError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setRiggingJobsError(null);
      try {
        const payload = await bakingApi.listRiggingJobs(projectId);
        if (!cancelled) setRiggingJobsSummary(payload);
      } catch (err) {
        if (!cancelled) {
          setRiggingJobsSummary(null);
          setRiggingJobsError(err.message || 'Failed to load rigging jobs');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, showRiggingJobs]);

  const latestReadyRevision = revisions
    .filter((revision) => revision.status === 'ready')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  const loadCompatibility = async (revisionId) => {
    if (!revisionId) {
      setCompatibility(null);
      setCompatibilityError(null);
      return;
    }
    setCompatibilityLoading(true);
    setCompatibilityError(null);
    try {
      const data = await bakingApi.getRevisionCompatibility(revisionId);
      setCompatibility(data);
    } catch (err) {
      if (err.status === 404 && err.code === 'compatibility_analysis_missing') {
        setCompatibility(null);
        setCompatibilityError('No compatibility analysis yet.');
      } else {
        setCompatibilityError(err.message || 'Failed to load compatibility');
      }
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const reload = async () => {
    const [projectData, revisionData, sceneData, jobData] = await Promise.all([
      bakingApi.getProject(projectId),
      bakingApi.listRevisions(projectId),
      bakingApi.listScenes(projectId),
      bakingApi.listJobs(),
    ]);
    setProject(projectData);
    setRevisions(revisionData);
    setScenes(sceneData);
    setJobs(jobData.filter((job) => job.project === projectId));
    const readyRevision = revisionData
      .filter((revision) => revision.status === 'ready')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    await loadCompatibility(readyRevision ? readyRevision.id : null);
  };

  const latestReadyRevisionId = latestReadyRevision ? latestReadyRevision.id : null;

  useEffect(() => {
    if (!latestReadyRevisionId) {
      setCompatibility(null);
      return undefined;
    }
    loadCompatibility(latestReadyRevisionId);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestReadyRevisionId]);

  const onAddScene = async (event) => {
    event.preventDefault();
    setSceneError(null);
    try {
      await bakingApi.createScene(projectId, {
        asset_path: assetPath,
        display_name: displayName,
      });
      await reload();
    } catch (err) {
      setSceneError(err.message || 'Failed to create scene');
    }
  };

  const onReanalyze = async () => {
    if (!latestReadyRevision) return;
    setReanalyzing(true);
    setCompatibilityError(null);
    try {
      const data = await bakingApi.analyzeRevisionCompatibility(latestReadyRevision.id, {
        force: true,
      });
      setCompatibility(data);
    } catch (err) {
      setCompatibilityError(err.message || 'Reanalysis failed');
    } finally {
      setReanalyzing(false);
    }
  };

  const compatibilityVariant =
    compatibility &&
    (compatibility.status === 'supported'
      ? 'success'
      : compatibility.status === 'conditionally_supported'
        ? 'warning'
        : 'danger');

  if (loading) {
    return (
      <div className="loading-state">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading project…
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  if (!project) {
    return <div className="empty-state">Project not found.</div>;
  }

  const mayaSummary =
    latestReadyRevision && latestReadyRevision.maya_manifest_summary
      ? latestReadyRevision.maya_manifest_summary
      : null;

  return (
    <div>
      <p className="mb-2">
        <Link to={`/console/projects/${projectId}`}>← Production</Link>
      </p>
      <h1 className="product-page-title">{project.name} · Project Settings</h1>
      <p className="mb-3 d-flex gap-3 flex-wrap" data-testid="project-section-nav">
        <Link
          to={`/console/ai?project=${projectId}${
            latestReadyRevision ? `&revision=${latestReadyRevision.id}` : ''
          }`}
          data-testid="ask-ai-technical-artist"
        >
          Ask AI Technical Artist
        </Link>
        {showBakeJobsCard && (
          <Link to="/console/jobs" data-testid="open-project-bake-jobs">
            Bake Jobs
          </Link>
        )}
        {showRiggingJobs && (
          <Link
            to={`/console/projects/${projectId}/rigging/jobs`}
            data-testid="open-project-rigging-jobs"
          >
            Rigging Jobs
          </Link>
        )}
        {showRiggingConfig && (
          <Link
            to={`/console/projects/${projectId}/rigging`}
            data-testid="open-project-rigging"
          >
            Rigging
          </Link>
        )}
      </p>
      <OpenRecommendationsPanel
        projectId={projectId}
        revisionId={latestReadyRevision ? latestReadyRevision.id : undefined}
      />
      <p className="product-page-subtitle d-flex align-items-center gap-2 flex-wrap">
        <ExecutionProviderBadge provider={project.execution_provider || 'unity'} />
        <span>
          {isMaya
            ? `Maya ${project.application_version || '(version unset)'}`
            : `${project.unity_version} / ${project.render_pipeline} ${project.render_pipeline_version}`}
        </span>
      </p>

      {isMaya && (
        <Alert variant="info" className="border">
          Static archive analysis is available for Maya projects. Maya execution is not
          enabled in this version.
        </Alert>
      )}

      <ProjectRevisionsPanel
        projectId={projectId}
        isMaya={isMaya}
        revisions={revisions}
        onReload={reload}
      />

      {isMaya && mayaSummary && (
        <div className="product-card" data-testid="maya-static-summary">
          <h2 className="h5">Static archive analysis</h2>
          <p className="text-muted">
            {jobCreationEnabled
              ? 'Static archive analysis complete. Runtime scene validation may be submitted when enabled.'
              : 'Static archive analysis complete. Maya execution is not enabled in this version.'}
          </p>
          <dl className="row mb-0">
            <dt className="col-sm-3">Workspace</dt>
            <dd className="col-sm-9 mono">
              {(mayaSummary.workspace && mayaSummary.workspace.root) || '—'}{' '}
              <span className="text-muted">
                (
                {(mayaSummary.workspace && mayaSummary.workspace.detection_method) ||
                  'unknown'}
                )
              </span>
            </dd>
            <dt className="col-sm-3">Scenes</dt>
            <dd className="col-sm-9">{mayaSummary.scene_count || 0}</dd>
            <dt className="col-sm-3">Maya versions</dt>
            <dd className="col-sm-9">
              {(mayaSummary.requirements &&
                (mayaSummary.requirements.maya_versions || []).join(', ')) ||
                '—'}
            </dd>
            <dt className="col-sm-3">Plugins</dt>
            <dd className="col-sm-9">
              {(mayaSummary.requirements &&
                (mayaSummary.requirements.plugins || []).join(', ')) ||
                '—'}
            </dd>
          </dl>
          {mayaSummary.scenes && mayaSummary.scenes.length > 0 && (
            <Table responsive className="product-table mt-3 mb-0">
              <thead>
                <tr>
                  <th>Scene</th>
                  <th>Format</th>
                  <th>Version</th>
                  <th>Warnings</th>
                </tr>
              </thead>
              <tbody>
                {mayaSummary.scenes.map((scene) => (
                  <tr key={scene.path}>
                    <td className="mono">{scene.path}</td>
                    <td>{scene.format}</td>
                    <td>{scene.declared_maya_version || '—'}</td>
                    <td>{(scene.warnings && scene.warnings.length) || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {(mayaSummary.warnings || []).length > 0 && (
            <Alert variant="warning" className="mt-3 mb-0">
              {(mayaSummary.warnings || []).slice(0, 5).map((w) => (
                <div key={`${w.code}-${w.path}-${w.message}`}>
                  <strong>{w.code}</strong>: {w.message}
                </div>
              ))}
            </Alert>
          )}
        </div>
      )}

      <div className="product-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h5 mb-0">{isMaya ? 'Static compatibility' : 'Compatibility'}</h2>
          {latestReadyRevision && (
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={reanalyzing || compatibilityLoading}
              onClick={onReanalyze}
            >
              {reanalyzing ? 'Reanalyzing…' : 'Reanalyze'}
            </Button>
          )}
        </div>
        {compatibilityLoading && <p className="text-muted">Loading…</p>}
        {compatibilityError && <div className="error-state">{compatibilityError}</div>}
        {compatibility && (
          <Alert variant={compatibilityVariant || 'secondary'}>
            <div>
              <strong>{compatibility.status}</strong>
              {compatibility.summary ? ` — ${compatibility.summary}` : ''}
            </div>
            {isMaya && (
              <div className="mt-2 small">
                {jobCreationEnabled
                  ? 'Static analysis does not execute Maya. Validate in Maya runs isolated worker validation when enabled; opening untrusted scenes still carries residual risk without an OS-level sandbox.'
                  : 'Analysis mode: static only. Maya was not executed.'}
              </div>
            )}
          </Alert>
        )}
      </div>

      <div className="product-card">
        <h2 className="h5">{isMaya ? 'Maya scenes' : 'Scenes'}</h2>
        <Table responsive className="product-table mb-3">
          <thead>
            <tr>
              <th>Path</th>
              <th>Name</th>
            </tr>
          </thead>
          <tbody>
            {scenes.map((scene) => (
              <tr key={scene.id}>
                <td className="mono">{scene.asset_path}</td>
                <td>{scene.display_name}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Form className="row g-2 align-items-end" onSubmit={onAddScene}>
          <div className="col-md-6">
            <Form.Label>Asset path</Form.Label>
            <Form.Control
              value={assetPath}
              onChange={(e) => setAssetPath(e.target.value)}
              placeholder={isMaya ? 'scenes/main.ma' : 'Assets/Scenes/Main.unity'}
              required
            />
          </div>
          <div className="col-md-4">
            <Form.Label>Display name</Form.Label>
            <Form.Control
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </div>
          <div className="col-md-2">
            <Button type="submit">Add</Button>
          </div>
        </Form>
        {sceneError && <div className="error-state mt-2">{sceneError}</div>}
      </div>

      {showBakeJobsCard && (
        <div className="product-card" data-testid="project-bake-jobs-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="h5 mb-0">Bake Jobs</h2>
            {jobCreationEnabled && (
              <Button
                as={Link}
                to={`/console/jobs/new?project=${projectId}`}
                size="sm"
                data-testid="maya-validate-action"
              >
                {isMaya ? runtimeLabel : 'New bake'}
              </Button>
            )}
          </div>
          {!jobCreationEnabled ? (
            <Alert variant="secondary" className="mb-0" data-testid="maya-jobs-blocked">
              {isMaya
                ? providerCaps && providerCaps.runtime_unavailable_message
                  ? providerCaps.runtime_unavailable_message
                  : 'Maya runtime validation is not enabled. No execute, export, or validate-in-Maya controls are available.'
                : 'Job creation is not enabled for this execution provider.'}
            </Alert>
          ) : jobs.length === 0 ? (
            <p className="text-muted mb-0">No bake jobs for this project.</p>
          ) : (
            <Table responsive className="product-table mb-0">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Scene</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td>
                      <Link to={`/console/jobs/${job.id}`}>
                        <StatusPill status={job.status} />
                      </Link>
                    </td>
                    <td className="mono">{job.scene_asset_path}</td>
                    <td>{new Date(job.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {showRiggingJobs && (
        <div className="product-card" data-testid="project-rigging-jobs-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="h5 mb-0">Rigging Jobs</h2>
            {canCreateRiggingJobs && (
              <Button
                as={Link}
                to={`/console/projects/${projectId}/rigging/jobs/new`}
                size="sm"
                data-testid="project-new-rigging-job"
              >
                New Rigging Job
              </Button>
            )}
          </div>
          <RiggingRuntimeNotice
            availability={riggingJobsSummary?.runtime_availability}
            className="mb-3"
          />
          {riggingJobsError ? (
            <div className="error-state">{riggingJobsError}</div>
          ) : !riggingJobsSummary ? (
            <p className="text-muted mb-0">Loading rigging jobs…</p>
          ) : (riggingJobsSummary.total_count || 0) === 0 ? (
            <div data-testid="project-rigging-jobs-empty">
              <p className="mb-1">
                <strong>No rigging jobs yet</strong>
              </p>
              <p className="text-muted mb-2">
                Create a rigging job from a published character Rig Memory version and an immutable
                project revision.
              </p>
              {canCreateRiggingJobs ? (
                <Button
                  as={Link}
                  to={`/console/projects/${projectId}/rigging/jobs/new`}
                  size="sm"
                  variant="outline-primary"
                >
                  New Rigging Job
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              <dl className="row mb-3 small">
                <dt className="col-sm-3">Total</dt>
                <dd className="col-sm-9">{riggingJobsSummary.total_count || 0}</dd>
                <dt className="col-sm-3">Queued</dt>
                <dd className="col-sm-9">
                  {(riggingJobsSummary.status_counts &&
                    riggingJobsSummary.status_counts.queued) ||
                    0}
                </dd>
                <dt className="col-sm-3">Running</dt>
                <dd className="col-sm-9">
                  {(riggingJobsSummary.status_counts &&
                    riggingJobsSummary.status_counts.running) ||
                    0}
                </dd>
                <dt className="col-sm-3">Succeeded</dt>
                <dd className="col-sm-9">
                  {(riggingJobsSummary.status_counts &&
                    riggingJobsSummary.status_counts.succeeded) ||
                    0}
                </dd>
                <dt className="col-sm-3">Failed</dt>
                <dd className="col-sm-9">
                  {(riggingJobsSummary.status_counts &&
                    riggingJobsSummary.status_counts.failed) ||
                    0}
                </dd>
              </dl>
              {riggingJobsSummary.latest_job ? (
                <div className="mb-2">
                  <div className="text-muted small mb-1">Latest job</div>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Link
                      to={`/console/projects/${projectId}/rigging/jobs/${riggingJobsSummary.latest_job.id}`}
                    >
                      {shortJobId(riggingJobsSummary.latest_job.id)}
                    </Link>
                    <RiggingJobStatusBadge
                      job={riggingJobsSummary.latest_job}
                      workerRuntimeEnabled={Boolean(
                        riggingJobsSummary.runtime_availability?.worker_runtime_enabled
                      )}
                    />
                    <span className="small">
                      {operationLabel(riggingJobsSummary.latest_job.operation)}
                    </span>
                    <span className="small text-muted">
                      {riggingJobsSummary.latest_job.character_display_name || ''}
                    </span>
                  </div>
                </div>
              ) : null}
              <Link
                to={`/console/projects/${projectId}/rigging/jobs`}
                className="small"
              >
                View all Rigging Jobs
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ProjectSettingsPage;
